import { PropsWithChildren, ReactElement, useEffect } from "react";

import { CssBaseline, StyledEngineProvider } from "@mui/material";

import { FoundationsThemeProvider } from "foundations/FoundationsThemeProvider";
import {
  useGetBrandingColors,
  useGetLogoBranding,
} from "generated/api/platform-branding/platform-branding";
import { useLanguage } from "mocks/LanguageHooks";
import { getDirection } from "lib/rtl/rtlLocales";

/**
 * The application-wide color palette. All components in the app should use these colors (through
 * theme) unless specified otherwise. Keep in mind that within the MUI theme there are more
 * colors than are listed here.
 */

/**
 * The customized Material UI {@link MuiV5ThemeProvider|ThemeProvider} for MUI V5+.
 */
export function MuiThemeProvider(props: PropsWithChildren): ReactElement {
  const { data: logos } = useGetLogoBranding();
  const { data: colors = [] } = useGetBrandingColors();
  const [lang] = useLanguage();
  const direction = getDirection(lang);

  // Drive document direction/lang from the current language so RTL languages flip layout.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("dir", direction);
    if (lang) {
      root.setAttribute("lang", lang);
    }
  }, [direction, lang]);

  // Sets the favicon based on the one configured in the enterprise, otherwise uses the default
  useEffect(() => {
    // We want to replace the icon if it's falsy
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const faviconUrl = logos?.faviconUrl || "/communitycliq-favicon.png";
    document.querySelector("link[rel=icon]")?.setAttribute("href", faviconUrl);
  }, [logos?.faviconUrl]);

  return (
    <StyledEngineProvider injectFirst>
      <FoundationsThemeProvider colors={colors} direction={direction}>
        <CssBaseline enableColorScheme>{props.children}</CssBaseline>
      </FoundationsThemeProvider>
    </StyledEngineProvider>
  );
}
